/**
 * VocabProgressCard — the chat-hub entry card for "Your vocabulary".
 *
 * Shows live mastery counts ("450 mastered · 12 due for review") and
 * navigates to the Vocabulary screen. Loads the summary on focus; while
 * unavailable (first load / error) it renders a neutral subtitle and
 * never blocks the hub (VS-002).
 *
 * Spec: docs/specs/mobile/vocabulary-screen.mobile.md
 */

import React, { useCallback } from 'react';
import { StyleSheet, Text, TouchableOpacity, View } from 'react-native';
import { useFocusEffect, useNavigation } from '@react-navigation/native';
import { useTranslation } from 'react-i18next';

import Ionicons from '../ui/Ionicons';
import { useDisplayTheme } from '../../hooks/useDisplayTheme';
import { useUserPreferencesStore } from '../../store/userPreferencesStore';
import { selectActiveCourseId } from '../../store/userPreferencesSelectors';
import {
  useVocabStore,
  selectVocabSummary,
  selectLoadVocabSummary,
} from '../../store/vocabStore';

export const VocabProgressCard = React.memo(function VocabProgressCard() {
  const { t } = useTranslation();
  const { theme, fontScale, ffs } = useDisplayTheme();
  const navigation = useNavigation();
  const activeCourseId = useUserPreferencesStore(selectActiveCourseId);
  const summary = useVocabStore(selectVocabSummary);
  const loadSummary = useVocabStore(selectLoadVocabSummary);

  useFocusEffect(
    useCallback(() => {
      if (activeCourseId) void loadSummary(activeCourseId);
    }, [activeCourseId, loadSummary]),
  );

  const handlePress = useCallback(() => {
    navigation.navigate('Vocabulary');
  }, [navigation]);

  const mastered = summary ? summary.vocab.mastered + summary.expressions.mastered : null;
  const due = summary ? summary.vocab.due + summary.expressions.due : null;
  const subtitle =
    mastered !== null && due !== null
      ? t('vocabulary.hubCard.counts', { mastered, due })
      : t('vocabulary.hubCard.subtitle');

  return (
    <TouchableOpacity
      testID="hub-card-vocabulary"
      accessibilityRole="button"
      accessibilityLabel={t('vocabulary.hubCard.title')}
      onPress={handlePress}
      activeOpacity={0.7}
      style={styles.touchable}
    >
      <View style={[styles.card, { backgroundColor: theme.card, borderColor: theme.border }]}>
        <Text
          allowFontScaling={false}
          accessible={false}
          importantForAccessibility="no"
          style={{ fontSize: 24 * fontScale }}
        >
          📚
        </Text>
        <View style={{ flex: 1 }}>
          <Text
            allowFontScaling={false}
            style={{ fontSize: 14 * fontScale, color: theme.text, fontWeight: '600', ...ffs }}
          >
            {t('vocabulary.hubCard.title')}
          </Text>
          <Text
            allowFontScaling={false}
            numberOfLines={1}
            style={{ fontSize: 12 * fontScale, color: theme.textSecondary, marginTop: 2, ...ffs }}
          >
            {subtitle}
          </Text>
        </View>
        <Ionicons name="chevron-forward" size={18 * fontScale} color={theme.textSecondary} />
      </View>
    </TouchableOpacity>
  );
});

const styles = StyleSheet.create({
  touchable: { width: '100%' },
  card: {
    flexDirection: 'row',
    alignItems: 'center',
    gap: 12,
    padding: 16,
    borderRadius: 16,
    borderWidth: 1,
  },
});
